import {
  EditPencil as EditPencilIcon,
  Mail as MailIcon,
  PageEdit as PageEditIcon,
  Phone as PhoneIcon,
} from 'iconoir-react'
import { capitalize, entries } from 'lodash'
import moment from 'moment'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import Button from '../Button'
import { ChevronToggle } from '../ChevronToggle'
import { FormFieldMappingForm } from '../mapping/FormFieldMappingForm'
import Spinner from '../Spinner'
import { Tooltip } from '../Tooltip'
import { ActivityCard } from './ActivityCard'
import { ActivityTags } from './ActivityTags'
import { KeyValueDrawer } from './KeyValueDrawer'
import { MatchActivityModal } from './MatchActivityModal'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useImmediateRequest } from 'app/hooks/useImmediateRequest'
import { useShowUtmFields } from 'app/hooks/useShowUtmFields'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { showModal } from 'app/lib/modal'
import { sanitizeName } from 'app/sanitize-name'
import type { ActivityJson } from 'app/types/entities'
import type { FormJson } from 'app/types/entities'
import { useWorkspace } from 'app/utils'
import { assertBoom } from 'lib/assertBoom'
import { defaultFormFieldMappings } from 'lib/formFieldDefaults'
import { decodeHtmlEntities, humanizeLabel, isMachineKey, machineKeyStem, sentenceCase } from 'lib/formFieldLabels'
import { logError, logWarn } from 'lib/logging'

type Props = {
  activity: ActivityJson
  alwaysOpen?: boolean
  isFirst?: boolean
  isLast?: boolean
  /** See `ActivityCard`. */
  hideHeader?: boolean
  singleActivity?: boolean
}

/** Answers longer than this (or containing a line break) render as full-width message blocks. */
const LONG_ANSWER_CHARS = 80
/**
 * "Questions" longer than this are vendor blurbs, not field names — label them
 * "Message" instead. Real form questions run up to ~120 chars; marketing
 * blurbs used as question text run well past it.
 */
const BLURB_LABEL_CHARS = 120
/** How many answers show before the "Show all" expander kicks in. */
const ANSWER_PREVIEW_COUNT = 8

type AnswerEntry = {
  question: string
  label: string
  answer: string
  isLong: boolean
  mappable: boolean
  machine: boolean
}
type MappingRow = { id?: string; rawKey: string; formField: string }

/** Title-case each word ("barbara ferns" → "Barbara Ferns") without startCase's punctuation-mangling. */
const titleCase = (value?: string): string =>
  (value ?? ``)
    .split(/\s+/)
    .map((word) => capitalize(word))
    .join(` `)

/**
 * Some vendors slugify the answer too ("i_have_a_few_questions_first"). Only
 * whole-value underscore slugs qualify: an underscore is unambiguously a
 * machine separator, while a lone lowercase word could be exactly what the
 * submitter typed. Emails, URLs, IDs, and anything with whitespace stay
 * verbatim, and camel humps are never split — an answer may legitimately
 * contain them ("iPhone", "McDonald").
 */
const SLUG_ANSWER = /^[a-z0-9]+(?:[_-][a-z0-9]+)+[?!.]?$/i
const humanizeAnswer = (value: string): string =>
  value.includes(`_`) && SLUG_ANSWER.test(value) ? sentenceCase(value.split(`_`).filter(Boolean)) : value

/**
 * Multi-part widgets (a JotForm "Other" option, an address block) store their
 * whole answer as a JSON blob (`{"other":"gummy"}`). Show what the patient
 * picked, not the encoding. Keys are the widget's internal slot names, so only
 * the values survive.
 */
const flattenJsonAnswer = (value: string): string => {
  if (!/^[[{]/.test(value)) return value
  let parsed: unknown
  try {
    parsed = JSON.parse(value)
  } catch (err) {
    // An answer that opens with a brace but isn't JSON is the submitter's own
    // text — show it verbatim, and leave a breadcrumb since it's unusual.
    logWarn(`Form answer looks like JSON but did not parse`, err)
    return value
  }
  const scalars = (input: unknown): string[] => {
    if (input === null || input === undefined || input === `` || input === false) return []
    if (Array.isArray(input)) return input.flatMap(scalars)
    if (typeof input === `object`) return Object.values(input as Record<string, unknown>).flatMap(scalars)
    return [String(input)]
  }
  const values = scalars(parsed)
  return values.length ? values.join(`, `) : value
}

/**
 * JotForm sends each answer twice: once under the question the patient read,
 * once under the field's internal unique name ("What Best Describes Your
 * Smile?" and "whatBest", both "gummy"). Drop the machine-keyed copy only when
 * a prose row carries the same answer AND its question starts with the same
 * stem — so two unrelated fields that happen to share an answer both survive.
 */
function dropMachineKeyDuplicates(entries: AnswerEntry[]): AnswerEntry[] {
  const prose = entries.filter((entry) => !entry.machine)
  if (!prose.length) return entries
  return entries.filter((entry) => {
    if (!entry.machine) return true
    const stem = machineKeyStem(entry.question)
    // Stems this short ("id", "q1") would prefix-match half the form.
    if (stem.length < 4) return true
    return !prose.some((twin) => twin.answer === entry.answer && machineKeyStem(twin.question).startsWith(stem))
  })
}

/**
 * Raw form keys arrive in whatever shape the client's form uses ("E-mail",
 * "Appointment Request"), while mappings may store them differently
 * ("email", "appointmentRequest"). Compare with case, spacing, and
 * punctuation stripped so cosmetic variants still match.
 */
const normalizeRawKey = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, ``)

/** Machine-shaped date formats raw form answers arrive in. */
const ANSWER_DATETIME_FORMATS = [`MM/DD/YYYY h:mm A`, `M/D/YYYY h:mm A`, `MM/DD/YYYY HH:mm`, `M/D/YYYY HH:mm`]
const ANSWER_DATE_FORMATS = [`MM/DD/YYYY`, `M/D/YYYY`, `YYYY-MM-DD`]

/**
 * Reformat answers that are entirely a date ("03/27/2026 10:44 AM") to the
 * app's standard display ("Mar 27, 2026 10:44 AM"). Strict whole-value
 * parsing only — a value that merely contains digits or slashes stays
 * verbatim. No timezone math: a form answer is wall-clock text, not an
 * instant.
 */
const normalizeDateAnswer = (value: string): string => {
  const dateTime = moment(value, ANSWER_DATETIME_FORMATS, true)
  if (dateTime.isValid()) return dateTime.format(`MMM D, YYYY h:mm A`)
  const dateOnly = moment(value, ANSWER_DATE_FORMATS, true)
  if (dateOnly.isValid()) return dateOnly.format(`MMM D, YYYY`)
  return value
}

/**
 * JotForm multi-field widgets store their whole answer as an HTML table of
 * label/value rows ("<tr><td>First Name: </td><td>Michael</td></tr>…"). When
 * every row parses as a two-cell pair, return the pairs so they render as
 * individual answers; otherwise null, and the value falls back to one text
 * block.
 */
const parseTableAnswer = (value: string): { question: string; answer: string }[] | null => {
  if (!/<table[\s>]/i.test(value)) return null
  const rows = value.match(/<tr[^>]*>.*?<\/tr>/gis)
  if (!rows) return null
  const pairs: { question: string; answer: string }[] = []
  for (const row of rows) {
    const cells = [...row.matchAll(/<t[dh][^>]*>(.*?)<\/t[dh]>/gis)].map((m) => m[1])
    if (cells.length !== 2) return null
    const question = cells[0]
      .replace(/<[^>]*>/g, ``)
      .replace(/:\s*$/, ``)
      .trim()
    if (!question) return null
    pairs.push({ question, answer: cells[1] })
  }
  return pairs
}

/**
 * Some JotForm answers arrive as whole HTML fragments — multi-field widgets
 * render an entire <table> ("<tr><td>First Name: </td><td>Michael</td></tr>…").
 * Turn row/paragraph/line-break boundaries into newlines BEFORE tags are
 * stripped, so the sub-fields keep their own lines instead of fusing into
 * one blob ("MichaelLast Name: Muehlberger").
 */
const htmlToText = (value: string): string =>
  value
    .replace(/<(?:br|hr)\s*\/?>/gi, `\n`)
    .replace(/<\/(?:tr|p|div|li|h[1-6])>/gi, `\n`)
    .replace(/<[^>]*>/g, ``)
    .replace(/[ \t]+\n/g, `\n`)
    .replace(/\n{2,}/g, `\n`)
    .trim()

/**
 * Click-id / UTM / URL answers are tracking metadata that some vendors submit
 * as raw form fields. They belong with the rest of the tracking data, not in
 * the patient's answers.
 */
const isTrackingKey = (normalizedKey: string): boolean =>
  normalizedKey === `fbclid` ||
  normalizedKey === `gclid` ||
  normalizedKey === `msclkid` ||
  normalizedKey.startsWith(`utm`) ||
  normalizedKey.endsWith(`url`)

/**
 * Which canonical form fields actually render a value somewhere on this card.
 * A raw answer is hidden only when its key is mapped to one of these AND the
 * target has a value — so mapped-but-undisplayed data (e.g. newPatient) never
 * disappears from the card.
 */
function renderedFieldTargets(form: FormJson, activity: ActivityJson): Record<string, boolean> {
  const hasName = !!form.patientName
  return {
    patientName: hasName,
    patientNameFirst: hasName,
    patientNameLast: hasName,
    email: !!form.email,
    phone: !!form.phone,
    referralURL: !!form.referralURL,
    channel: !!(form.channel || activity.channel),
  }
}

/**
 * One question → answer pair from the submission, rendered like a line of a
 * form-notification email: muted question and value-weight answer share one
 * inline text flow, so long questions wrap as ordinary text. Long answers get
 * a quote box under their own label line. Blurb-length "questions" (vendor
 * marketing text) collapse to a "Message" label with the full text on hover.
 */
function AnswerRow({
  label,
  answer,
  isLong,
  action,
  unmapped = false,
  onMap,
  phi = true,
}: {
  label: string
  answer: string
  isLong: boolean
  action?: React.ReactNode
  unmapped?: boolean
  onMap?: () => void
  phi?: boolean
}) {
  const isBlurb = label.length > BLURB_LABEL_CHARS
  // Blurb "questions" collapse to a short "Message" label with the full text
  // on hover; real questions render whole and wrap as ordinary text, so
  // nothing truncates mid-word.
  const labelEl = isBlurb ? (
    <Tooltip
      placement="top"
      message={<div className="max-w-sm break-words text-body-xs text-strong">{label}</div>}
      keepOpen
      className="text-body-xs text-subtle"
    >
      Message
    </Tooltip>
  ) : (
    <span className="text-body-xs text-subtle">{label}</span>
  )
  // Mapping chrome (the Unmapped chip / hover pencil) sits at the row's far
  // edge, outside the text flow, so it can never dangle onto its own line.
  const actionEl = unmapped ? (
    <Tooltip message="This field has no mapping. Click to map it to a Trackable field so it can be extracted.">
      <button
        type="button"
        className="shrink-0 rounded-full bg-amber-50 px-1.5 py-px text-caption-md text-amber-700 hover:bg-amber-100"
        onClick={onMap}
      >
        Unmapped
      </button>
    </Tooltip>
  ) : (
    action
  )
  // Each answer is a ruled row, like one row of a form-notification email
  // table — the divider binds question to answer no matter how their widths
  // vary. Long answers want the full card width, so the label keeps its own
  // line inside the row.
  if (isLong) {
    return (
      <div className="group flex flex-col gap-y-1 py-2 first:pt-0 last:pb-0">
        <div className="flex items-baseline justify-between gap-x-2">
          <div className="min-w-0 break-words">{labelEl}</div>
          {actionEl}
        </div>
        <div className="phi whitespace-pre-wrap break-words rounded-md border-l-2 border-strong bg-surface-subtle px-3 py-2 text-ui-xs leading-relaxed text-strong">
          {answer}
        </div>
      </div>
    )
  }
  // Label and value share one inline text flow, like a line of an email —
  // a long question wraps as ordinary text and the answer continues right
  // after it. Adjacency pairs them; weight contrast separates them.
  return (
    <div className="group flex items-baseline justify-between gap-x-3 py-2 first:pt-0 last:pb-0">
      <div className="min-w-0 break-words leading-relaxed">
        {labelEl}
        {` `}
        <span className={`${phi ? `phi ` : ``}ml-1 text-ui-xs text-strong`}>{answer}</span>
      </div>
      {actionEl}
    </div>
  )
}

/**
 * Renders Form activity record as three tiers: who submitted and why (canonical
 * mapped fields), what they said (dynamic raw answers), and collapsed tracking
 * details (vendor/UTM/URLs).
 * @Prop activity - Activity to query the Form from.
 * @Prop alwaysOpen. Boolean. If true it hides drawer button and shows the content.
 * @Prop isFirst. Boolean. If true it hides the first activity union line.
 * @Prop isLast. Boolean. If true it hides the last activity union line.
 * @returns ReactElement
 */
export function FormCard({ activity, alwaysOpen, isFirst, isLast, singleActivity, hideHeader }: Props): ReactElement {
  const workspace = useWorkspace()
  const showUtmFields = useShowUtmFields()
  const checkPermission = useCheckPermission()
  const canEditMapping = checkPermission([`org:settings:edit-mapping`, `settings:mapping:edit`])
  assertBoom(activity.type === `Form`)
  // Errors render inline on the card (no global toast), so dead records —
  // e.g. forms deleted when an integration was removed — stay quiet.
  // Activities without a linked record (typeId null) skip the fetch entirely;
  // interpolating would request /Form/null and 404.
  const hasRecordId = Boolean(activity.typeId)
  const [response, responseError, refetch] = useImmediateRequest<{ record: FormJson }>(
    hasRecordId ? `/api/activities/${activity.id}/Form/${activity.typeId}` : ``,
    false,
  )
  const form = useMemo(() => response?.record, [response])
  // responseError is an Error instance; lodash isEmpty() would call it empty
  // and leave the card spinning forever.
  const isLoading = hasRecordId && response === undefined && responseError === undefined
  // Only a 404 means the record is gone. Anything else (dev's episodic
  // org-context 500s, network hiccups) is transient — offer a retry instead
  // of claiming the data no longer exists.
  const recordIsGone = /record not found/i.test(responseError instanceof Error ? responseError.message : ``)

  const [customMappings, setCustomMappings] = useState<MappingRow[]>([])
  const [trackingOpen, setTrackingOpen] = useState(false)
  const [showAllAnswers, setShowAllAnswers] = useState(false)

  useEffect(() => {
    let isMounted = true
    gqlFetchList(`formFieldMappings`, { pagination: { limit: 500 } })
      .then(({ hits }) => {
        if (!isMounted) return
        setCustomMappings(hits.map((m) => ({ id: m.id, rawKey: m.rawKey ?? ``, formField: m.formField ?? `` })))
      })
      .catch((err) => logError(`Error fetching form field mappings`, err))
    return () => {
      isMounted = false
    }
  }, [])

  // Every mapped raw key (normalized), regardless of whether its canonical
  // target renders — used to flag the answers nobody has mapped yet.
  const allMappedKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const mapping of [...defaultFormFieldMappings, ...customMappings]) {
      keys.add(normalizeRawKey(mapping.rawKey))
    }
    return keys
  }, [customMappings])

  const visibleAnswers = useMemo((): AnswerEntry[] => {
    if (!form?.answers) return []
    const rendered = renderedFieldTargets(form, activity)
    const hiddenKeys = new Set<string>()
    for (const mapping of [...defaultFormFieldMappings, ...customMappings]) {
      if (rendered[mapping.formField]) hiddenKeys.add(normalizeRawKey(mapping.rawKey))
    }
    const allAnswers = entries(form.answers)
      .flatMap(([question, answer]) => {
        const raw = String(answer ?? ``).trim()
        // Multi-field table answers explode into their sub-fields. The
        // sub-keys aren't stored raw keys, so mappings can't target them.
        const pairs = parseTableAnswer(raw)
        if (pairs) return pairs.map((pair) => ({ ...pair, mappable: false }))
        return [{ question, answer: raw, mappable: true }]
      })
      .map(({ question, answer, mappable }) => {
        const rawLabel = decodeHtmlEntities(sanitizeName(question))
        return {
          question,
          mappable,
          machine: isMachineKey(rawLabel),
          label: humanizeLabel(rawLabel),
          answer: normalizeDateAnswer(
            humanizeAnswer(decodeHtmlEntities(sanitizeName(htmlToText(flattenJsonAnswer(answer))))),
          ),
        }
      })
      .filter(({ question, answer }) => !!answer && !hiddenKeys.has(normalizeRawKey(question)))
      .map((entry) => ({
        ...entry,
        isLong: entry.answer.length > LONG_ANSWER_CHARS || entry.answer.includes(`\n`),
      }))
    return dropMachineKeyDuplicates(allAnswers)
  }, [form, activity, customMappings])

  // Raw tracking answers often just repeat what the canonical fields already
  // extracted (gclid, referrer URL). Only relocated answers that carry a
  // different value earn their own tracking row.
  const canonicalTrackingValues = useMemo(() => {
    if (!form) return new Set<string>()
    const values = [
      form.channel || activity.channel,
      form.utmSource,
      form.utmMedium,
      form.utmCampaign,
      form.utmContent,
      form.utmKeywords,
      form.gclid,
      form.referralURL,
      form.landingPageURL,
    ]
    return new Set(values.filter(Boolean).map((value) => String(value).trim()))
  }, [form, activity])

  const trackingAnswers = useMemo(
    () =>
      visibleAnswers.filter(
        (a) => isTrackingKey(normalizeRawKey(a.question)) && !canonicalTrackingValues.has(a.answer.trim()),
      ),
    [visibleAnswers, canonicalTrackingValues],
  )
  // Submission order, like a form-notification email — no regrouping.
  const orderedAnswers = useMemo(
    () => visibleAnswers.filter((a) => !isTrackingKey(normalizeRawKey(a.question))),
    [visibleAnswers],
  )
  // Collapsing a single answer costs a click and saves no space — the
  // expander only engages when it hides at least two.
  const collapseAnswers = orderedAnswers.length > ANSWER_PREVIEW_COUNT + 1
  const shownAnswers =
    showAllAnswers || !collapseAnswers ? orderedAnswers : orderedAnswers.slice(0, ANSWER_PREVIEW_COUNT)

  // The email's subject line: the form's name, minus the practice-name prefix
  // JotForm titles carry ("Hamblin Family Dentistry - Implant Survey").
  const subject = useMemo(() => {
    if (!form?.title) return undefined
    const title = decodeHtmlEntities(sanitizeName(form.title))
    const company = form.companyName?.trim()
    if (company && title.toLowerCase().startsWith(company.toLowerCase())) {
      const stripped = title
        .slice(company.length)
        .replace(/^[\s\-–—|:]+/, ``)
        .trim()
      if (stripped) return stripped
    }
    return title
  }, [form])

  const showMatchingModal = () => {
    if (!form) return
    showModal(<MatchActivityModal form={form} activity={activity} />, {
      title: `Manage Activity Link`,
      isFullScreen: false,
      is80Percent: true,
      removePadding: true,
      useRadix: true,
    })
  }

  const showMappingModal = (rawKey: string) => {
    const existingMapping = customMappings.find((m) => normalizeRawKey(m.rawKey) === normalizeRawKey(rawKey))
    const isEdit = !!existingMapping
    showModal(
      <FormFieldMappingForm
        mapping={existingMapping ?? { rawKey, formField: `` }}
        isDefault={false}
        successMessage={
          isEdit
            ? `Mapping updated. Forms will be reprocessed shortly.`
            : `Mapping created. Forms will be reprocessed shortly.`
        }
      />,
      {
        title: isEdit ? `Update Form Field Mapping` : `Create New Form Field Mapping`,
        isFullScreen: false,
        removePadding: true,
        useRadix: true,
      },
    )
  }

  const mappingAction = (question: string) => {
    const isMapped = allMappedKeys.has(normalizeRawKey(question))
    return (
      <Tooltip message={isMapped ? `Update mapping for this field` : `Create mapping for this field`}>
        <EditPencilIcon
          className="size-4 shrink-0 cursor-pointer text-minimal opacity-0 transition-opacity hover:text-default group-hover:opacity-100"
          strokeWidth={2}
          onClick={() => showMappingModal(question)}
        />
      </Tooltip>
    )
  }

  // Gravity ingestion stores a literal "Unknown" when no name field matched —
  // an absent name reads better than a patient named Unknown.
  const displayName = useMemo(() => {
    const name = titleCase(sanitizeName(form?.patientName)).trim()
    return /^unknown$/i.test(name) ? `` : name
  }, [form])

  // Purely tracking/attribution data. Form Title and Submission Date are
  // gone on purpose: the subject line and the card's header date already
  // show them.
  const trackingMap = form
    ? {
        Channel: form.channel || activity.channel,
        ...(showUtmFields && { Source: form.utmSource || `-` }),
        ...(showUtmFields && { Medium: form.utmMedium || `-` }),
        ...(showUtmFields && { Campaign: form.utmCampaign || `-` }),
        ...(showUtmFields && { Content: form.utmContent || `-` }),
        ...(showUtmFields && { Keywords: form.utmKeywords || `-` }),
        ...(showUtmFields && { GCLID: form.gclid }),
        ...(showUtmFields && { 'Referral URL': form.referralURL || `-` }),
        ...(showUtmFields && { 'Submission URL': form.landingPageURL || `-` }),
        ...trackingAnswers.reduce((acc, { label, answer }) => ({ ...acc, [label]: answer }), {}),
      }
    : {}
  const hasTracking = Object.values(trackingMap).some(Boolean)

  return (
    <ActivityCard
      hideHeader={hideHeader}
      title={
        <span className="text-ui-sm text-strong">
          Form <span className="text-body-sm text-subtle">Submission</span>
        </span>
      }
      activityDate={activity?.activityDate}
      icon={<PageEditIcon className="h-5 w-5 rounded-md bg-inverse p-1 text-inverse" />}
      tags={<ActivityTags activityTags={activity?.tags} workspaceId={workspace?.id} isLoading={isLoading} />}
      drawer={
        <div className="w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          {isLoading ? (
            <Spinner size={4} />
          ) : (
            <>
              {form && (
                <div className="flex flex-col gap-y-3">
                  {/* From block, like an email header: sender, contact line, subject */}
                  <div className="flex min-w-0 flex-col gap-y-0.5">
                    <div className="flex items-center justify-between gap-x-2">
                      {displayName && (
                        <span className="phi truncate text-ui-sm font-medium text-strong">{displayName}</span>
                      )}
                      {form.status && (
                        <span className="ml-auto shrink-0 rounded-full bg-surface-muted px-2 py-0.5 text-caption-md text-default">
                          {capitalize(form.status)}
                        </span>
                      )}
                    </div>
                    {(form.phone || form.email) && (
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-body-xs text-subtle">
                        {form.phone && (
                          <a href={`tel:${form.phone}`} className="phi flex items-center gap-x-1 hover:text-strong">
                            <PhoneIcon className="size-3.5 shrink-0 text-minimal" strokeWidth={2} />
                            {formatPhoneNumber(form.phone)}
                          </a>
                        )}
                        {form.email && (
                          <a
                            href={`mailto:${form.email}`}
                            className="phi flex min-w-0 items-center gap-x-1 hover:text-strong"
                          >
                            <MailIcon className="size-3.5 shrink-0 text-minimal" strokeWidth={2} />
                            <span className="truncate">{form.email}</span>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  {(subject || shownAnswers.length > 0) && (
                    <div className="flex flex-col divide-y divide-subtle border-t border-subtle pt-3">
                      {subject && <AnswerRow label="Form" answer={subject} isLong={false} phi={false} />}
                      {shownAnswers.map(({ question, label, answer, isLong, mappable }, index) => (
                        <AnswerRow
                          key={`${question}-${index}`}
                          label={label}
                          answer={answer}
                          isLong={isLong}
                          action={mappable && canEditMapping ? mappingAction(question) : undefined}
                          unmapped={mappable && canEditMapping && !allMappedKeys.has(normalizeRawKey(question))}
                          onMap={() => showMappingModal(question)}
                        />
                      ))}
                      {collapseAnswers && (
                        <button
                          type="button"
                          className="self-start pt-2 text-body-xs text-subtle hover:text-default"
                          onClick={() => setShowAllAnswers(!showAllAnswers)}
                        >
                          {showAllAnswers ? `Show fewer answers` : `Show all ${orderedAnswers.length} answers`}
                        </button>
                      )}
                    </div>
                  )}
                  <div className="flex flex-col gap-y-3 border-t border-subtle pt-2">
                    <div className="flex items-center justify-between">
                      {hasTracking ? (
                        <button
                          type="button"
                          className="flex items-center gap-x-1 text-body-xs text-subtle hover:text-default"
                          onClick={() => setTrackingOpen(!trackingOpen)}
                        >
                          Tracking details
                          <ChevronToggle open={trackingOpen} className="pr-0" chevronClassName="size-4" />
                        </button>
                      ) : (
                        <span />
                      )}
                      <Button
                        label="Manage"
                        mode="white"
                        type="button"
                        className="!text-trn-black h-6 !px-3 !text-ui-xs"
                        onClick={showMatchingModal}
                        disabled={!form}
                      />
                    </div>
                    {trackingOpen && hasTracking && (
                      <KeyValueDrawer
                        cols={2}
                        map={trackingMap}
                        // Relocated raw answers are still submission data —
                        // tracking URLs can carry emails in query params
                        className={trackingAnswers.reduce((acc, { label }) => ({ ...acc, [label]: `phi` }), {})}
                      />
                    )}
                  </div>
                </div>
              )}
              {!form &&
                (responseError && !recordIsGone ? (
                  <div className="flex w-full items-center justify-center gap-x-2 py-1 text-body-xs text-subtle">
                    Form details couldn't be loaded.
                    <button
                      type="button"
                      className="font-medium text-default underline-offset-2 hover:text-strong hover:underline"
                      onClick={() => {
                        refetch().catch((err) => logError(`Error retrying form fetch`, err))
                      }}
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <div className="w-full py-1 text-center text-body-xs text-subtle">
                    Form details are no longer available for this submission.
                  </div>
                ))}
            </>
          )}
        </div>
      }
      alwaysOpen={alwaysOpen}
      isFirst={isFirst}
      isLast={isLast}
      singleActivity={singleActivity}
    />
  )
}
